import { queryClient } from 'Context/QueryClientContext';
import { FILES_QUERY_KEY } from 'Data/keys/QueryKeys';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import { BucketFileStatusEnum } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { ICheckUploadFilesResponse } from 'Data/interfaces/Upload/ICheckUploadFilesResponse';
import { IUploadFileData } from 'Data/interfaces/Upload/IUploadFileData';

const isRendering = (status?: BucketFileStatusEnum | null): boolean =>
  status === BucketFileStatusEnum.Rendering;

// O CheckUploadFiles aponta qual arquivo a nova revisão vai tornar obsoleto
// (`FileIdObsolet`), mas não devolve o status de renderização dele — hoje
// `RenderingStatus` e `Versions` voltam null. Quem tem o status é a lista de
// arquivos da pasta, no cache do React Query.
//
// A lista está carregada nos dois fluxos de upload: pela listagem, é a pasta que
// o usuário está olhando; pelo viewer federado, o FileDestination consulta a
// pasta de destino com o mesmo `useQueryFiles`. E o status fica fresco porque o
// `useQueryFiles` coloca todo arquivo em `Rendering` no polling de progresso.
const isObsoleteFileRendering = (
  checkUpload: ICheckUploadFilesResponse,
  csId?: number,
  folderId?: string,
): boolean => {
  if (!checkUpload.FileIdObsolet || !csId || !folderId) {
    return false;
  }

  const cached = queryClient.getQueryData<IUseQueryFilesReturn>(
    [FILES_QUERY_KEY, Number(csId), folderId],
  );

  const obsoleteFile = cached?.ListFiles?.find(file =>
    (file.Identifier?.ApiId || file.Identifier?.ItemId) === checkUpload.FileIdObsolet);

  return isRendering(obsoleteFile?.RenderingStatus);
};

// Uma nova revisão só pode subir depois que a versão anterior terminar de
// renderizar: as duas disputariam a mesma conversão e o arquivo poderia acabar
// apontando para o artefato da versão errada.
//
// O status da versão anterior é procurado em três lugares, porque nenhum deles
// cobre todos os fluxos:
//
// - `RenderingStatus` do próprio retorno e o de cada item de `Versions` — hoje o
//   backend manda os dois null, mas ficam aqui para quando passar a preenchê-los;
// - o arquivo apontado por `FileIdObsolet`, resolvido no cache da listagem. É o
//   único que funciona na revisão por nomenclatura (MOD1 → MOD2), em que
//   `FileNameExist` vem falso e `Versions` vem vazio.
//
// Basta um acusar `Rendering` para bloquear.
export const isPreviousVersionRendering = (
  uploadFile?: IUploadFileData,
): boolean => {
  const checkUpload = uploadFile?.checkUploadFileInfo;

  if (!checkUpload) {
    return false;
  }

  return isRendering(checkUpload.RenderingStatus)
    || (checkUpload.Versions || []).some(version => isRendering(version.RenderingStatus))
    || isObsoleteFileRendering(checkUpload, uploadFile.csId, uploadFile.folderId);
};
